import { LogOut, MoreHorizontal, Settings, Target } from "lucide-react";
import { useMoneyFormat } from "@/hooks/use-money-format";
import {
  CanAccess,
  useCanAccess,
  useGetIdentity,
  useLocaleState,
  useLogout,
  useTranslate,
} from "ra-core";
import { Link, useNavigate } from "react-router";

import { useTheme } from "@/components/admin/use-theme";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { nextTheme, themeOption } from "../../misc/themeCycle";
import { ACCOUNT_MENU_ENTRIES } from "./accountMenu";
import { RailTooltip } from "./DeskNav";
import { ICON_STROKE } from "./navHelpers";
import { initials } from "./shellModel";
import { useMonthGoal } from "./useShellData";

const useMoney = () => {
  const { formatMoney } = useMoneyFormat();
  // Whole units: the goal card is a glance, not an invoice.
  return (value: number) => formatMoney(Math.round(value));
};

/** Monthly goal: won vs. expected amount of the deals closing this month. */
const StatusCard = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const [locale = "es"] = useLocaleState();
  const money = useMoney();
  const { goal, isPending } = useMonthGoal();
  const month = new Intl.DateTimeFormat(locale, { month: "short" })
    .format(new Date())
    .replace(".", "");
  const percent = Math.round(goal.ratio * 100);
  const isEmpty = !isPending && goal.count === 0;

  return (
    <section
      className="desk-status-card"
      aria-label={translate("desk.shell.goal.title")}
    >
      <header className="desk-status-card__head">
        <Target aria-hidden className="size-4" strokeWidth={ICON_STROKE} />
        <span className="desk-status-card__title">
          {translate("desk.shell.goal.title")}
        </span>
        <span className="desk-status-card__tag">{month}</span>
      </header>
      <div className="desk-status-card__progress">
        <span className="desk-status-card__value">
          {isPending
            ? "—"
            : translate("desk.shell.goal.percent_won", { percent })}
        </span>
        <span
          className="desk-status-card__track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <span
            className="desk-status-card__bar"
            style={{ transform: `scaleX(${isPending ? 0 : goal.ratio})` }}
          />
        </span>
      </div>
      <p className="desk-status-card__text">
        {isPending ? (
          " "
        ) : isEmpty ? (
          translate("desk.shell.goal.empty")
        ) : (
          <>
            {translate("desk.shell.goal.won_prefix")}{" "}
            <strong>{money(goal.won)}</strong> {translate("desk.shell.goal.of")}{" "}
            <strong>{money(goal.expected)}</strong>{" "}
            {translate("desk.shell.goal.expected_suffix")}
          </>
        )}
      </p>
      <button
        type="button"
        className="desk-status-card__button desk-press"
        onClick={() => navigate("/deals")}
      >
        {translate("desk.shell.goal.cta")}
      </button>
    </section>
  );
};

/**
 * Theme control: cycles light → dark → system on the same store setting as
 * the account settings (useTheme), with the same labels and icons.
 */
const ThemeCycleButton = ({ collapsed }: { collapsed: boolean }) => {
  const translate = useTranslate();
  const { theme, setTheme } = useTheme();
  const current = themeOption(theme);
  const next = themeOption(nextTheme(theme));
  const value = translate(current.labelKey);
  const label = translate("crm.theme.current", { theme: value });
  const Icon = current.icon;
  return (
    <RailTooltip label={label} enabled={collapsed}>
      <button
        type="button"
        aria-label={label}
        title={translate("crm.theme.switch_to", {
          theme: translate(next.labelKey),
        })}
        className="desk-nav__item desk-press"
        onClick={() => setTheme(next.value)}
      >
        <span className="desk-nav__icon" aria-hidden>
          <Icon className="size-4" strokeWidth={ICON_STROKE} />
        </span>
        <span className="desk-nav__label">{translate("crm.theme.label")}</span>
        <span
          className="desk-nav__label ml-auto text-xs text-muted-foreground"
          aria-hidden
        >
          {value}
        </span>
      </button>
    </RailTooltip>
  );
};

const UtilityLink = ({
  to,
  icon: Icon,
  label,
  collapsed,
}: {
  to: string;
  icon: typeof Settings;
  label: string;
  collapsed: boolean;
}) => (
  <RailTooltip label={label} enabled={collapsed}>
    <Link
      to={to}
      className="desk-nav__item desk-press"
      aria-label={collapsed ? label : undefined}
    >
      <span className="desk-nav__icon" aria-hidden>
        <Icon className="size-4" strokeWidth={ICON_STROKE} />
      </span>
      <span className="desk-nav__label">{label}</span>
    </Link>
  </RailTooltip>
);

const LogoutButton = ({ collapsed }: { collapsed: boolean }) => {
  const translate = useTranslate();
  const logout = useLogout();
  const label = translate("ra.auth.logout");
  return (
    <RailTooltip label={label} enabled={collapsed}>
      <button
        type="button"
        className="desk-nav__item desk-press"
        aria-label={collapsed ? label : undefined}
        onClick={() => logout()}
      >
        <span className="desk-nav__icon" aria-hidden>
          <LogOut className="size-4" strokeWidth={ICON_STROKE} />
        </span>
        <span className="desk-nav__label">{label}</span>
      </button>
    </RailTooltip>
  );
};

const AccountMenuItem = ({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: typeof Settings;
  label: string;
}) => (
  <DropdownMenuItem
    render={<Link to={to} className="flex items-center gap-2" />}
  >
    <Icon strokeWidth={ICON_STROKE} />
    {label}
  </DropdownMenuItem>
);

/** User card: opens the account menu (the old header user menu). */
const UserCard = ({ collapsed }: { collapsed: boolean }) => {
  const translate = useTranslate();
  const logout = useLogout();
  const { identity } = useGetIdentity();
  const name = identity?.fullName ?? "";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="desk-user-card desk-press"
        aria-label={translate("desk.shell.account_menu")}
        title={collapsed ? name : undefined}
      >
        <span className="desk-user-card__avatar" aria-hidden>
          {identity?.avatar ? (
            <img src={identity.avatar} alt="" />
          ) : (
            initials(name)
          )}
        </span>
        <span className="desk-user-card__name">{name}</span>
        <MoreHorizontal
          aria-hidden
          className="desk-user-card__more size-4"
          strokeWidth={ICON_STROKE}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={collapsed ? "right" : "top"}
        align={collapsed ? "end" : "start"}
        sideOffset={8}
        className="w-56"
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel className="truncate">{name}</DropdownMenuLabel>
          {ACCOUNT_MENU_ENTRIES.map(
            ({ to, icon, label, labelArgs, access }) => {
              const item = (
                <AccountMenuItem
                  key={to}
                  to={to}
                  icon={icon}
                  label={translate(label, labelArgs)}
                />
              );
              return access ? (
                <CanAccess
                  key={to}
                  resource={access.resource}
                  action={access.action}
                >
                  {item}
                </CanAccess>
              ) : (
                item
              );
            },
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()}>
          <LogOut strokeWidth={ICON_STROKE} />
          {translate("ra.auth.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const DeskSidebarFooter = ({ collapsed }: { collapsed: boolean }) => {
  const translate = useTranslate();
  const { canAccess: canEditConfiguration } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });
  return (
    <div className="desk-sidebar__bottom">
      {collapsed ? null : <StatusCard />}
      <div className="desk-sidebar__utilities">
        <ThemeCycleButton collapsed={collapsed} />
        <UtilityLink
          to={canEditConfiguration ? "/settings" : "/profile"}
          icon={Settings}
          label={translate("desk.shell.settings")}
          collapsed={collapsed}
        />
        <LogoutButton collapsed={collapsed} />
      </div>
      <div className="desk-sidebar__separator" role="separator" />
      <UserCard collapsed={collapsed} />
    </div>
  );
};
